import { create } from 'zustand';

// ============================================
// 漫游全局状态：轨道编辑 / 第一人称漫游 / 路径巡航 互斥
// 路径相机数据本体在 sceneStore.pathCameras（持久化），此处仅存 UI/播放态
// ============================================

export type RoamMode = 'orbit' | 'firstPerson';

interface RoamState {
  mode: RoamMode;
  /** WASD：true=飞行穿墙 / false=步行贴地 */
  isFlyMode: boolean;
  /** 基础移动速度 */
  baseSpeed: number;
  /** 人眼高度（步行贴地） */
  eyeHeight: number;
  /** 鼠标灵敏度 */
  mouseSensitivity: number;
  /** 相机锁定状态（PointerLock） */
  pointerLocked: boolean;
  /** 2026-08-26｜第一人称自动游览：沿场景 waypoints 路线自动步行（镜头人视高度贴地），
   * 按 WASD 任意键自动退出（转为手动控制） */
  autoWalk: boolean;

  // ---- 路径相机（轨道相机）UI/播放态 ----
  /** 当前激活编辑的路径相机 */
  activePathCameraId: string | null;
  /** 当前选中的关键帧下标（属性面板编辑用） */
  selectedKeyFrameIndex: number | null;
  /** 轨道相机实体（相机本体图标）是否被左键选中 → 显示 gizmo 可平移/旋转 */
  pathCameraEntitySelected: boolean;
  /** 是否正在播放路径巡航 */
  isPathPlaying: boolean;
  /** 播放当前时间（秒） */
  playTime: number;
  /** 是否显示路径相机辅助对象（路径线/关键帧/视锥） */
  showPathHelpers: boolean;

  setMode: (mode: RoamMode) => void;
  toggleFlyMode: () => void;
  setEyeHeight: (h: number) => void;
  /** 2026-10-16（P1-3 第一人称属性完善）：移动速度/鼠标灵敏度（运行时设置） */
  setBaseSpeed: (s: number) => void;
  setMouseSensitivity: (s: number) => void;
  setPointerLocked: (locked: boolean) => void;
  setAutoWalk: (on: boolean) => void;
  resetRoamView: () => void;

  setActivePathCamera: (id: string | null) => void;
  setSelectedKeyFrame: (index: number | null) => void;
  setPathCameraEntitySelected: (selected: boolean) => void;
  setPathPlaying: (playing: boolean) => void;
  setPlayTime: (t: number) => void;
  toggleShowPathHelpers: () => void;
}

export const useRoamStore = create<RoamState>((set) => ({
  mode: 'orbit',
  isFlyMode: false,
  baseSpeed: 3,
  eyeHeight: 2,
  mouseSensitivity: 0.002,
  pointerLocked: false,
  autoWalk: false,

  activePathCameraId: null,
  selectedKeyFrameIndex: null,
  pathCameraEntitySelected: false,
  isPathPlaying: false,
  playTime: 0,
  showPathHelpers: true,

  setMode: (mode) => set({ mode }),
  toggleFlyMode: () => set((s) => ({ isFlyMode: !s.isFlyMode })),
  setEyeHeight: (h) => set({ eyeHeight: h }),
  setBaseSpeed: (s) => set({ baseSpeed: Math.min(100, Math.max(0.1, s)) }),
  setMouseSensitivity: (s) => set({ mouseSensitivity: Math.min(0.02, Math.max(0.0001, s)) }),
  setPointerLocked: (locked) => set({ pointerLocked: locked }),
  setAutoWalk: (on) => set({ autoWalk: on }),
  resetRoamView: () => {
    // 外层组件注册具体实现（复用 cameraControlsRegistry.resetCamera）
    window.dispatchEvent(new CustomEvent('roam-reset-view'));
  },

  setActivePathCamera: (id) => set({ activePathCameraId: id, selectedKeyFrameIndex: null, pathCameraEntitySelected: false }),
  setSelectedKeyFrame: (index) => set({ selectedKeyFrameIndex: index, pathCameraEntitySelected: false }),
  setPathCameraEntitySelected: (selected) => set({ pathCameraEntitySelected: selected }),
  setPathPlaying: (playing) => set({ isPathPlaying: playing, playTime: 0 }),
  setPlayTime: (t) => set({ playTime: t }),
  toggleShowPathHelpers: () => set((s) => ({ showPathHelpers: !s.showPathHelpers })),
}));
